/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { ExternalStyles, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { RadioGroupField, RadioGroupFieldProps } from 'components/form'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'

export interface RadioGroupBoxFieldProps<T extends string | number | string[]>
  extends Pick<RadioGroupFieldProps<T>, 'name' | 'options' | 'required'> {
  label: string
  disabled?: boolean
  tooltipText?: string
  labelStyle?: ExternalStyles
}

export function RadioGroupBoxField<T extends string | number | string[]>(props: Readonly<RadioGroupBoxFieldProps<T>>) {
  const { name, label, options, required, disabled, tooltipText, labelStyle } = props

  const theme = useTheme()
  const styles = createStyles(theme, disabled)

  return (
    <Tooltip text={tooltipText}>
      <Box style={styles.box}>
        <VFlow vSpacing={0.5}>
          {required ? (
            <RequiredLabel
              color={disabled ? 'disabled' : 'normal'}
              fontWeight='bold'
              label={label}
              style={labelStyle}
            />
          ) : (
            <Text color={disabled ? 'disabled' : 'normal'} fontWeight='bold' style={labelStyle}>
              {label}
            </Text>
          )}
          <RadioGroupField<T>
            name={name}
            options={options}
            required={required}
            lines={options.length}
            style={{ radioField: styles.radioGroup }}
            disabled={disabled}
          />
        </VFlow>
      </Box>
    </Tooltip>
  )
}

const createStyles = (theme: Theme, isDisabled: boolean) => ({
  box: css`
    border-color: ${theme.pallete.gray.c80};
    background: ${isDisabled ? theme.pallete.gray.c90 : theme.pallete.surface.main};
  `,
  radioGroup: css`
    margin-left: -0.025rem;
  `,
})
